import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Globe } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useGlobal } from "@/context/GlobalContext";
import { CURRENCIES } from "@/lib/global";

const LANGS = [
  { code: "en", name: "English" },
  { code: "es", name: "Español" },
  { code: "fr", name: "Français" },
  { code: "pt", name: "Português" },
];

const ALL_TZ = typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") : [];

const Row = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="overline text-slate-400">{label}</Label>
    {children}
  </div>
);

export const GlobalSettings = () => {
  const { t } = useTranslation();
  const g = useGlobal();
  const [open, setOpen] = useState(false);
  const tzList = useMemo(() => (ALL_TZ.includes(g.timezone) ? ALL_TZ : [g.timezone, ...ALL_TZ]), [g.timezone]);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="global-settings-btn"
        aria-label={t("global.settings")}
        className="h-9 px-2.5 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 inline-flex items-center gap-1.5 text-xs font-mono uppercase transition-colors"
      >
        <Globe size={15} />
        <span>{g.language}</span>
        <span className="text-slate-500">·</span>
        <span>{g.currency}</span>
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="global-settings-dialog" className="bg-[#0B0F17] border-white/10 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-xl">{t("global.settings")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Row label={t("global.language")}>
              <Select value={g.language} onValueChange={g.setLanguage}>
                <SelectTrigger data-testid="global-language-select" className="bg-[#131A26] border-white/10 h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LANGS.map((l) => <SelectItem key={l.code} value={l.code} data-testid={`lang-option-${l.code}`}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Row>
            <Row label={t("global.currency")}>
              <Select value={g.currency} onValueChange={g.setCurrency}>
                <SelectTrigger data-testid="global-currency-select" className="bg-[#131A26] border-white/10 h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((c) => <SelectItem key={c.code} value={c.code} data-testid={`currency-option-${c.code}`}>{c.code} · {c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Row>
            <Row label={t("global.timezone")}>
              <Select value={g.tzPref} onValueChange={g.setTimezone}>
                <SelectTrigger data-testid="global-timezone-select" className="bg-[#131A26] border-white/10 h-11"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="auto" data-testid="tz-option-auto">{t("global.auto_tz")} — {g.deviceTz}</SelectItem>
                  {tzList.map((tz) => <SelectItem key={tz} value={tz} data-testid={`tz-option-${tz}`}>{tz.replace(/_/g, " ")}</SelectItem>)}
                </SelectContent>
              </Select>
            </Row>
            <p className="text-xs text-slate-500" data-testid="global-charged-note">{t("global.charged_in")}</p>
            <button
              onClick={() => setOpen(false)}
              data-testid="global-settings-done"
              className="w-full h-11 rounded-xl bg-rose-500 hover:bg-rose-400 font-bold transition-colors"
            >
              {t("global.done")}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
